// Utility to normalize Slack-like markup into GitHub-Flavored Markdown
// Handles links, mentions, channel references, bullets, and section headers

export type MentionResolver = (id: string) => string;

export function normalizeSlackToMarkdown(
  input: string,
  options?: { resolveMention?: MentionResolver }
): string {
  const resolveMention: MentionResolver = options?.resolveMention || ((id) => `@${id}`);

  let out = input || '';

  // <https://url|label> → [label](https://url)
  out = out.replace(/<((?:https?:\/\/)[^>|]+)\|([^>]+)>/g, '[$2]($1)');

  // Bare <https://url> → <https://url> (react-markdown will auto-link if enabled, but keep as-is)
  // No change needed; included for clarity

  // Mentions: <@U123ABC> → @name (fallback @U123ABC)
  out = out.replace(/<@([A-Z0-9]+)>/g, (_m, id: string) => resolveMention(id));

  // Optional: bare @token → @resolved(token) if a resolver is provided and token looks like an ID-like word
  // Guard against emails/usernames with dot characters by restricting to [A-Za-z0-9_\-]+
  if (options?.resolveMention) {
    out = out.replace(
      /(^|\s)@([A-Za-z0-9_-]+)\b/g,
      (_m, prefix: string, token: string) => `${prefix}${resolveMention(token)}`
    );
  }

  // Channels: <#C123|channel-name> → #channel-name
  out = out.replace(/<#([A-Z0-9]+)\|([^>]+)>/g, '#$2');

  // Special entities like <!here> or <!channel> → @here / @channel
  out = out.replace(/<!([^>|]+)>/g, (_m, word: string) => `@${word}`);
  out = out.replace(/<!([^>|]+)\|([^>]+)>/g, (_m, _code: string, label: string) => `@${label}`);

  // Bullets: leading "•" to markdown list
  out = out.replace(/^\s*•\s+/gm, '- ');

  // Section headers like *Summary* as entire line → ### Summary
  out = out.replace(/^\s*\*([^\n*]+)\*\s*$/gm, '### $1');

  return out;
}
